import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Users, MessageSquare } from "lucide-react";

export default function Communities() {
  const [list, setList] = useState([]);
  const [active, setActive] = useState(null);
  const [posts, setPosts] = useState([]);
  const [text, setText] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/communities"); setList(data.communities || []); }
    catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (c) => {
    try {
      if (c.joined) await api.post(`/communities/${c.slug}/leave`);
      else await api.post(`/communities/${c.slug}/join`);
      await load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const open = async (c) => {
    setActive(c);
    try { const { data } = await api.get(`/communities/${c.slug}/posts`); setPosts(data.posts || []); }
    catch (e) { setErr(formatApiError(e)); }
  };

  const post = async (e) => {
    e.preventDefault();
    if (!text.trim() || !active) return;
    setBusy(true);
    try { await api.post(`/communities/${active.slug}/posts`, { text, community_id: active.slug }); setText(""); await open(active); }
    catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-widest text-rose">Communities</div>
        <h1 className="font-display text-5xl text-navy leading-tight">Meet naturally through what you love.</h1>
        <p className="text-navy/70 mt-2">Camping, cooking, books, travel — the best relationships often start beside a shared passion.</p>
      </div>

      {!active && (
        <div className="grid md:grid-cols-3 gap-4">
          {list.map((c) => (
            <div key={c.slug} className="glass rounded-3xl p-6" data-testid={`community-${c.slug}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-display text-2xl text-navy">{c.name}</div>
                  <div className="text-sm text-navy/60">{c.desc}</div>
                </div>
                <div className="text-xs text-navy/60 flex items-center gap-1"><Users size={12} /> {c.members}</div>
              </div>
              <div className="mt-5 flex gap-2">
                <button data-testid={`join-${c.slug}`} onClick={() => toggle(c)}
                  className={`btn-pill flex-1 text-sm ${c.joined ? "bg-white/70 border border-navy/10 text-navy" : "bg-navy text-cream-100 hover:bg-navy-600"}`}>
                  {c.joined ? "Leave" : "Join"}
                </button>
                <button data-testid={`open-${c.slug}`} onClick={() => open(c)}
                  className="btn-pill text-sm bg-rose text-white hover:bg-rose-600 flex items-center gap-1">
                  <MessageSquare size={14} /> Open
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {active && (
        <div className="max-w-3xl mx-auto">
          <button data-testid="community-back" onClick={() => setActive(null)} className="text-sm text-navy/60 hover:text-navy">← All communities</button>
          <div className="glass rounded-3xl p-8 mt-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-display text-3xl text-navy">{active.name}</div>
                <div className="text-navy/60 text-sm">{active.desc}</div>
              </div>
              <button data-testid={`join2-${active.slug}`} onClick={() => toggle(active)}
                className={`btn-pill text-sm ${active.joined ? "bg-white/70 border border-navy/10 text-navy" : "bg-navy text-cream-100"}`}>
                {active.joined ? "Leave" : "Join"}
              </button>
            </div>
            <form onSubmit={post} className="mt-6 space-y-3">
              <textarea data-testid="community-post-input" value={text} onChange={(e) => setText(e.target.value)} rows={3}
                className="w-full px-4 py-3 rounded-2xl bg-white/80 border border-navy/10"
                placeholder="Share a story, a tip, or a moment from your passion…" />
              <div className="flex justify-end">
                <button data-testid="community-post-submit" type="submit" disabled={busy || !text.trim()}
                  className="btn-pill bg-rose text-white hover:bg-rose-600 disabled:opacity-50">
                  {busy ? "Sharing..." : "Share"}
                </button>
              </div>
            </form>
            <div className="mt-6 space-y-3">
              {posts.map((p) => (
                <div key={p.id} className="rounded-2xl bg-white/70 border border-white/70 p-4" data-testid={`post-${p.id}`}>
                  <div className="text-sm text-navy">{p.text}</div>
                  <div className="text-[10px] text-navy/50 mt-1">— {p.author} · {new Date(p.at).toLocaleString()}</div>
                </div>
              ))}
              {posts.length === 0 && <div className="text-sm text-navy/50">Be the first to share.</div>}
            </div>
          </div>
        </div>
      )}

      {err && <div className="text-sm text-rose-600" data-testid="communities-error">{err}</div>}
    </div>
  );
}
